/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    Button,
    Heading,
    useBreakpointValue,
    ChakraProvider
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useAuthHelper, AuthHelpers} from '@salesforce/commerce-sdk-react'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'
import theme from '../../../theme'
import {setSessionJSONItem, buildRedirectURI} from '../../../utils/utils'
import {useSiteCustomPreferences} from '../../../hooks/use-site-custom-preferences'
import {useCurrentCustomer} from '../../../hooks/use-current-customer'

/**
 * Back Ground Image with text component
 *
 * @param {object} props
 * @param {string} - props.heading - Text Below Image.
 * @param {string} - props.heading - Text Overlay.
 * @param {string} - props.altText - The image alt text shown by the component.
 * @returns {React.ReactElement} - ImageWithText component.
 */
export const JoinPremiumAndPurchases = ({
    description,
    descriptionTextfontWeight,
    descriptionTextStyle,
    descriptionTextColor,
    containerPosition,
    heading,
    headingTextColor,
    headingTextStyle,
    headingTextfontWeight,
    buttonText,
    buttonLink,
    buttonfontWeight,
    buttonBackgroundColor,
    buttonTextColor,
    buttonfontStyle,
    fansPremiumPlansDiscount,
    fansPremiumPlansDiscountText,
    fansPremiumPlansDiscountTextColor,
    megaFansPremiumPlansDiscount,
    megaFansPremiumPlansDiscountText,
    megaFansPremiumPlansDiscountColor,
    megaFansPremiumPlansDiscountTextColor,
    ultimateFansPremiumPlansDiscount,
    ultimateFansPremiumPlansDiscountText,
    ultimateFansPremiumPlansDiscountTextColor,
    custom,
    buttonTarget,
    alreadyJoinedPreLinkText = null,
    alreadyJoinedLinkText = null,
    alreadyJoinedPostLinkText = null
}) => {
    const showBgImage = useBreakpointValue({base: true})
    const isMobile = useBreakpointValue({base: true, sm: false})
    const [bgImage, setBgImage] = useState('')
    const containerRef = useRef(null)
    const imageURL = custom?.backgroundImage
    const plansIcon = custom?.plansIcon
    const segment = useSegment()
    const targetLink = buttonTarget === 'New-Window' ? '_blank' : '_self'
    // sso-login
    const authorizeIDP = useAuthHelper(AuthHelpers.AuthorizeIDP)
    const appOrigin = useAppOrigin()
    const configs = getConfig()
    const {locale, site} = useMultiSite()
    const redirectPath = configs?.app?.login?.social?.redirectURI || ''
    const redirectURI = buildRedirectURI(appOrigin, redirectPath, site, locale)
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const {data: customer} = useCurrentCustomer()
    const ssoEnable = getcustomPreferencevalue('pwaSSOEnable')
    const onSocialLoginClick = async () => {
        try {
            // Save the path where the user logged in
            setSessionJSONItem('returnToPage', window.location.pathname)
            await authorizeIDP.mutateAsync({
                hint: configs?.app?.login?.social?.ssoIDPName,
                redirectURI: redirectURI
            })
        } catch (error) {
            console.error('Error in onSocialLoginClick:', error)
        }
    }

    useEffect(() => {
        if (!showBgImage) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // Lazy-load background image
                    if (isMobile) {
                        setBgImage(
                            `url(${getAssetUrl('static/img/bg_premium_banner_mobile_overlay.jpg')})`
                        )
                    } else {
                        setBgImage(`url(${imageURL?.disBaseLink})`)
                    }
                    observer.disconnect() // stop observing once loaded
                }
            },
            {
                rootMargin: '300px' // load before it fully enters the viewport
            }
        )

        if (containerRef.current) {
            observer.observe(containerRef.current)
        }

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current)
            }
        }
    }, [showBgImage, isMobile, imageURL])

    const customerInfo = {
        isFan: customer?.c_customerGroups?.includes('Fan') || false,
        isMegaFan: customer?.c_customerGroups?.includes('Mega Fan') || false,
        isUltimateFan: customer?.c_customerGroups?.includes('Ultimate Fan') || false
    }
    const joinPremiumAndPurchases = {
        '.join-premium-text-container.left': {
            clipPath: {base: 'polygon(0 0, 94% 0, 100% 100%, 0% 100%)'},
            background: {
                base: 'unset',
                sm: 'linear-gradient(268deg, transparent 15px, hsla(0, 0%, 100%, .5647058824) .1%)'
            }
        },
        '.join-premium-text-container.right': {
            float: 'right',
            clipPath: {base: 'polygon(5% 0%, 100% 0, 100% 100%, 0 100%)'},
            background: {
                base: 'unset',
                sm: 'linear-gradient(268deg, transparent 15px, hsla(0, 0%, 100%, .5647058824) .1%)'
            }
        },
        '.alreadyjoin-text a': {
            backgroundColor: 'unset !important',
            textDecoration: 'underline'
        }
    }

    const sanitizeText = (html) => {
        return html.replace(/^<p>(.*)<\/p>$/s, '$1')
    }

    return (
        <ChakraProvider theme={theme}>
            <Box
                ref={containerRef}
                position="relative"
                data-testid="join-premium-and-purchases-bg"
                width="100%"
                backgroundImage={bgImage}
                backgroundRepeat={'no-repeat'}
                backgroundPosition={{base: 'center', sm: '60%', xl: 'unset'}}
                backgroundSize={'cover'}
                overflow="hidden"
                clear="both"
                borderTop="1px solid rgba(74, 78, 88, 0.30)"
                maxWidth={{base: '100%', sm: '100%', md: '100%', lg: '100%', xl: '1920px'}}
                mx={'auto'}
                sx={joinPremiumAndPurchases}
            >
                <Box borderRadius="md">
                    <Box
                        className={`join-premium-text-container ${containerPosition}`}
                        w={{base: '100%', sm: '62%', md: '55%'}}
                        p={{base: '32px 16px', sm: '48px 32px', md: '48px 64px'}}
                        background={{base: '#faf7ef', sm: '#faf7ef1a'}}
                        sx={{
                            '@media screen and (min-width: 48em)': {
                                backgroundSize: '100% 100%',
                                backgroundRepeat: 'no-repeat'
                            }
                        }}
                    >
                        {heading && (
                            <Heading
                                as="h2"
                                color={headingTextColor?.value || 'black'}
                                fontWeight={
                                    headingTextfontWeight === 'bold' ? 800 : headingTextfontWeight
                                }
                                fontStyle={headingTextStyle}
                                fontSize={{base: '32px', lg: '64px'}}
                                lineHeight={{base: '40px', lg: '72px'}}
                            >
                                {heading}
                            </Heading>
                        )}
                        {description && (
                            <Text
                                color={descriptionTextColor?.value || 'black'}
                                fontWeight={
                                    descriptionTextfontWeight === 'bold'
                                        ? 800
                                        : descriptionTextfontWeight
                                }
                                fontStyle={descriptionTextStyle}
                                fontSize={{base: 'md', xl: 'xl'}}
                                lineHeight={{base: '6', xl: '8'}}
                                py={6}
                                px={0}
                            >
                                {description}
                            </Text>
                        )}
                        <Box>
                            {!customerInfo.isFan &&
                                !customerInfo.isMegaFan &&
                                !customerInfo.isUltimateFan &&
                                fansPremiumPlansDiscountText && (
                                    <Box mb={8} display="flex" alignItems="center" clear="both">
                                        <Box
                                            position="relative"
                                            bg={`url(${plansIcon?.disBaseLink})`}
                                            w={'72px'}
                                            h="70px"
                                            backgroundSize="72px 70px"
                                            display="flex"
                                            justify="center"
                                            align="center"
                                        >
                                            <Heading
                                                as="h3"
                                                fontSize="xl"
                                                fontWeight="bold"
                                                position="absolute"
                                                top="50%"
                                                left="50%"
                                                transform="translate(-50%, -50%)"
                                                color="white"
                                            >
                                                {fansPremiumPlansDiscount}
                                            </Heading>
                                        </Box>
                                        <Heading
                                            as="h3"
                                            fontSize={{base: '24px', xl: '32px'}}
                                            lineHeight={{base: '32px', xl: '40px'}}
                                            fontWeight="extrabold"
                                            width={`calc(100% - 70px)`}
                                            pl={4}
                                            color={fansPremiumPlansDiscountTextColor?.value}
                                        >
                                            {fansPremiumPlansDiscountText}
                                        </Heading>
                                    </Box>
                                )}
                            {!customerInfo.isMegaFan &&
                                !customerInfo.isUltimateFan &&
                                megaFansPremiumPlansDiscountText && (
                                    <Box mb={8} display="flex" alignItems="center" clear="both">
                                        <Box
                                            position="relative"
                                            bg={`url(${plansIcon?.disBaseLink})`}
                                            w={'72px'}
                                            h="70px"
                                            backgroundSize="72px 70px"
                                            display="flex"
                                            justify="center"
                                            align="center"
                                        >
                                            <Heading
                                                fontSize="xl"
                                                fontWeight="bold"
                                                position="absolute"
                                                top="50%"
                                                left="50%"
                                                transform="translate(-50%, -50%)"
                                                color={
                                                    megaFansPremiumPlansDiscountColor?.value ||
                                                    'white'
                                                }
                                                as="h3"
                                            >
                                                {megaFansPremiumPlansDiscount}
                                            </Heading>
                                        </Box>
                                        <Heading
                                            fontSize={{base: '24px', xl: '32px'}}
                                            lineHeight={{base: '32px', xl: '40px'}}
                                            fontWeight="extrabold"
                                            width={`calc(100% - 70px)`}
                                            pl={4}
                                            color={
                                                megaFansPremiumPlansDiscountTextColor?.value ||
                                                'bleck'
                                            }
                                            as="h3"
                                        >
                                            {megaFansPremiumPlansDiscountText}
                                        </Heading>
                                    </Box>
                                )}
                            {ultimateFansPremiumPlansDiscountText && (
                                <Box mb={8} display="flex" alignItems="center" clear="both">
                                    <Box
                                        position="relative"
                                        bg={`url(${plansIcon?.disBaseLink})`}
                                        w={'72px'}
                                        h="70px"
                                        backgroundSize="72px 70px"
                                        display="flex"
                                        justify="center"
                                        align="center"
                                    >
                                        <Heading
                                            fontSize="xl"
                                            fontWeight="bold"
                                            position="absolute"
                                            top="50%"
                                            left="50%"
                                            transform="translate(-50%, -50%)"
                                            color="white"
                                            as="h3"
                                        >
                                            {ultimateFansPremiumPlansDiscount}
                                        </Heading>
                                    </Box>
                                    <Heading
                                        fontSize={{base: '24px', xl: '32px'}}
                                        lineHeight={{base: '32px', xl: '40px'}}
                                        fontWeight="extrabold"
                                        width={`calc(100% - 70px)`}
                                        pl={4}
                                        color={ultimateFansPremiumPlansDiscountTextColor?.value}
                                        as="h3"
                                    >
                                        {ultimateFansPremiumPlansDiscountText}
                                    </Heading>
                                </Box>
                            )}
                        </Box>
                        {buttonText && (
                            <Button
                                as="a"
                                href={buttonLink}
                                target={targetLink}
                                mb={8}
                                size="lg"
                                p={{base: '20px 96px'}}
                                bg={buttonBackgroundColor?.value}
                                borderRadius="8px"
                                color={buttonTextColor?.value}
                                fontWeight={buttonfontWeight === 'bold' ? 800 : buttonfontWeight}
                                fontStyle={buttonfontStyle}
                                fontSize="24px"
                                lineHeight="32px"
                                textTransform="capitalize"
                                height="auto"
                                _hover={{bg: buttonBackgroundColor?.value}}
                                onClick={(event) => {
                                    // segment
                                    segment.sendPanelSelectedEvent(event, {})
                                }}
                            >
                                {buttonText}
                            </Button>
                        )}

                        {/* {alreadyjoined && (
                            <Box
                                fontWeight="medium"
                                color="black"
                                bg="none"
                                className="alreadyjoin-text"
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeText(alreadyjoined)
                                }}
                            />
                        )} */}

                        {customer?.isGuest && (
                            <Box display={'flex'}>
                                {alreadyJoinedPreLinkText && (
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: sanitizeText(alreadyJoinedPreLinkText)
                                        }}
                                    />
                                )}
                                {alreadyJoinedLinkText && (
                                    <Link
                                        mx={'2px'}
                                        textDecoration="underline"
                                        href={ssoEnable ? '#' : '/login'}
                                        onClick={() => {
                                            if (ssoEnable) onSocialLoginClick()
                                        }}
                                    >
                                        {alreadyJoinedLinkText}
                                    </Link>
                                )}
                                {alreadyJoinedPostLinkText && (
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: sanitizeText(alreadyJoinedPostLinkText)
                                        }}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </ChakraProvider>
    )
}

JoinPremiumAndPurchases.propTypes = {
    ITCLink: PropTypes.string,
    heading: PropTypes.string,
    containerPosition: PropTypes.string,
    contentMaxWidth: PropTypes.string,
    altText: PropTypes.string,
    description: PropTypes.string,
    descriptionTextfontWeight: PropTypes.string,
    descriptionTextStyle: PropTypes.string,
    descriptionTextColor: PropTypes.object,
    backgroundColor: PropTypes.string,
    buttonText: PropTypes.string,
    buttonLink: PropTypes.string,
    buttonfontWeight: PropTypes.string,
    buttonTarget: PropTypes.string,
    buttonBackgroundColor: PropTypes.object,
    buttonTextColor: PropTypes.object,
    buttonfontStyle: PropTypes.string,
    categoryLink: PropTypes.string,
    imageToRight: PropTypes.bool,
    buttonSize: PropTypes.string,
    fansPremiumPlansDiscount: PropTypes.string,
    fansPremiumPlansDiscountText: PropTypes.string,
    fansPremiumPlansDiscountTextColor: PropTypes.object,
    megaFansPremiumPlansDiscount: PropTypes.string,
    megaFansPremiumPlansDiscountColor: PropTypes.object,
    megaFansPremiumPlansDiscountText: PropTypes.string,
    megaFansPremiumPlansDiscountTextColor: PropTypes.object,
    ultimateFansPremiumPlansDiscount: PropTypes.string,
    ultimateFansPremiumPlansDiscountText: PropTypes.string,
    ultimateFansPremiumPlansDiscountTextColor: PropTypes.object,
    headingTextColor: PropTypes.object,
    headingTextStyle: PropTypes.string,
    headingTextfontWeight: PropTypes.string,
    custom: PropTypes.object,
    alreadyJoinedPreLinkText: PropTypes.string,
    alreadyJoinedLinkText: PropTypes.string,
    alreadyJoinedPostLinkText: PropTypes.string
}

export default JoinPremiumAndPurchases
